import http from './http'

const EdamamService = {

  /**
   * Get autocomplete suggestions for food search
   * @param {string} query - The search query (e.g., "chi" for "chicken")
   * @param {number} limit - Maximum number of suggestions (1-10)
   * @returns {Promise<Array<string>>} Array of food name suggestions
   */
  autocomplete(query, limit = 10) {
    if (!query || query.trim() === '') {
      return Promise.resolve([])
    }

    return http.get('/edamam/autocomplete', {
      params: {
        q: query.trim(),
        limit: Math.min(Math.max(limit, 1), 10)
      }
    }).then(response => response.data)
      .catch(error => {
        console.error('Edamam autocomplete error:', error)
        return []
      })
  },

  /**
   * Search for foods using the parser endpoint
   * @param {string} query - The food search query (e.g., "chicken breast")
   * @param {object} options - Optional filters
   * @param {string} options.nutritionType - "cooking" or "logging" (default: "logging")
   * @param {array} options.health - Health labels to filter by
   * @param {string} options.calories - Calorie range (e.g., "100-300")
   * @param {array} options.category - Food categories
   * @param {object} options.nutrients - Nutrient filters (e.g., { PROCNT: "10+" })
   * @returns {Promise<object>} Parser response with parsed foods and hints
   */
  searchFoods(query, options = {}) {
    if (!query || query.trim() === '') {
      return Promise.reject(new Error('Query is required'))
    }

    const params = {
      ingr: query.trim(),
      'nutrition-type': options.nutritionType || 'logging'
    }

    if (options.health) {
      params.health = options.health
    }

    if (options.calories) {
      params.calories = options.calories
    }

    if (options.category) {
      params.category = options.category
    }

    // Add nutrient filters
    if (options.nutrients && typeof options.nutrients === 'object') {
      Object.keys(options.nutrients).forEach(nutrient => {
        params[`nutrients[${nutrient}]`] = options.nutrients[nutrient]
      })
    }

    return http.get('/edamam/search', { params })
      .then(response => response.data)
  },

  /**
   * Get detailed nutrition data for specific food(s)
   * @param {array} ingredients - Array of ingredient objects
   * @param {string} ingredients[].food_id - Edamam food ID
   * @param {number} ingredients[].quantity - Quantity of the food
   * @param {string} ingredients[].measure_uri - Edamam measure URI
   * @returns {Promise<object>} Nutrition data with calories, macros, micronutrients, etc.
   */
  getNutrition(ingredients) {
    if (!ingredients || !Array.isArray(ingredients) || ingredients.length === 0) {
      return Promise.reject(new Error('Ingredients array is required'))
    }

    // Validate ingredients
    for (let i = 0; i < ingredients.length; i++) {
      const ingredient = ingredients[i]
      if (!ingredient.food_id && !ingredient.foodId) {
        return Promise.reject(new Error(`Ingredient at index ${i} is missing food_id`))
      }
      const qty = ingredient.quantity
      if (qty == null || qty <= 0) {
        return Promise.reject(new Error(`Ingredient at index ${i} must have a positive quantity`))
      }
      if (!ingredient.measure_uri && !ingredient.measureURI) {
        return Promise.reject(new Error(`Ingredient at index ${i} is missing measure_uri`))
      }
    }

    return http.post('/edamam/nutrition', { ingredients })
      .then(response => response.data)
  },

  /**
   * Helper: Extract nutrition data from Edamam response and format for journal entry
   * @param {object} nutritionData - Response from getNutrition()
   * @returns {object} Formatted nutrition data for journal entry
   */
  formatNutritionForEntry(nutritionData) {
    if (!nutritionData || !nutritionData.totalNutrients) {
      return null
    }

    const nutrients = nutritionData.totalNutrients

    return {
      calories: Math.round(nutrients.ENERC_KCAL?.quantity || 0),
      protein: parseFloat((nutrients.PROCNT?.quantity || 0).toFixed(1)),
      carbs: parseFloat((nutrients.CHOCDF?.quantity || 0).toFixed(1)),
      fat: parseFloat((nutrients.FAT?.quantity || 0).toFixed(1)),
      fiber: parseFloat((nutrients.FIBTG?.quantity || 0).toFixed(1)),
      sugar: parseFloat((nutrients.SUGAR?.quantity || 0).toFixed(1)),
      sodium: Math.round(nutrients.NA?.quantity || 0)
    }
  },

  /**
   * Helper: Get the first parsed food result from search response
   * @param {object} searchResult - Response from searchFoods()
   * @returns {object|null} First parsed food or null
   */
  getFirstParsedFood(searchResult) {
    if (!searchResult || !searchResult.parsed || searchResult.parsed.length === 0) {
      return null
    }
    return searchResult.parsed[0]
  },

  /**
   * Helper: Get food hints (suggestions) from search response
   * @param {object} searchResult - Response from searchFoods()
   * @param {number} limit - Maximum number of hints to return
   * @returns {array} Array of food hints
   */
  getFoodHints(searchResult, limit = 10) {
    if (!searchResult || !searchResult.hints) {
      return []
    }
    return searchResult.hints.slice(0, limit)
  },

  /**
   * Helper: Extract measures from a food object
   * @param {object} food - Food object from parser response
   * @returns {array} Array of measure objects
   */
  getMeasures(food) {
    if (!food || !food.measures) {
      return []
    }
    return food.measures
  },

  /**
   * Helper: Find a specific measure by label
   * @param {array} measures - Array of measure objects
   * @param {string} label - Measure label to find (e.g., "Cup", "Ounce")
   * @returns {object|null} Measure object or null
   */
  findMeasureByLabel(measures, label) {
    if (!measures || !Array.isArray(measures)) {
      return null
    }
    return measures.find(m => m && m.label && m.label.toLowerCase() === label.toLowerCase()) || null
  },

  /**
   * Helper: Get serving size measure (usually the first measure or one labeled "Serving")
   * @param {array} measures - Array of measure objects
   * @returns {object|null} Serving measure object or null
   */
  getServingMeasure(measures) {
    if (!measures || !Array.isArray(measures) || measures.length === 0) {
      return null
    }

    // Try to find "Serving" measure (guard against missing label)
    const servingMeasure = measures.find(m =>
      m && m.label && m.label.toLowerCase().includes('serving')
    )

    // Otherwise return first measure
    return servingMeasure || measures[0]
  }
}

export default EdamamService
